/**
 * 多图轮播 style.carouselExt 解析与默认值（与 docs/Carousel-component-json.md 一致）
 *
 * schemaVersion 2：移除 fadeDots / arrows / coverFlow，新增 peekScale / deck / film / marquee / mosaic。
 * 老数据兼容：coverFlow → peekScale；fadeDots / arrows → legacy。
 * 尺寸兜底：组件宽高低于样式阈值时统一降级 legacy，Flutter 端使用同一张阈值表。
 */

import Vue from "vue";

export const CAROUSEL_EXT_SCHEMA_VERSION = 2;

export const CAROUSEL_PLAY_MODES = ["legacy", "auto", "manual", "autoAndManual"];

/** 新样式（顺序即编辑器下拉顺序） */
export const CAROUSEL_STYLES = [
  "legacy",
  "peekScale",
  "deck",
  "film",
  "marquee",
  "mosaic",
];

/** schemaVersion 1 的历史样式名 → 新样式名 */
export const CAROUSEL_STYLE_ALIASES = {
  coverFlow: "peekScale",
  fadeDots: "legacy",
  arrows: "legacy",
};

const DEFAULT_TRANSITION = {
  durationMs: 450,
  type: "fade",
};

export function ensureCarouselExt(style) {
  if (!style || typeof style !== "object") {
    return {};
  }
  if (!style.carouselExt || typeof style.carouselExt !== "object") {
    return {};
  }
  return style.carouselExt;
}

/** 无扩展 / play 与 style 均为 legacy → 走 §2.5 legacy 渲染器 */
export function isLegacyCarousel(style) {
  const ext = ensureCarouselExt(style);
  if (!ext || Object.keys(ext).length === 0) {
    return true;
  }
  const play = ext.playMode || "legacy";
  const visual = normalizeCarouselStyle(ext.carouselStyle);
  return play === "legacy" && visual === "legacy";
}

/** 任意历史值 → 合法样式名 */
export function normalizeCarouselStyle(raw) {
  const v = String(raw == null ? "" : raw).trim();
  if (CAROUSEL_STYLES.indexOf(v) > -1) {
    return v;
  }
  if (CAROUSEL_STYLE_ALIASES[v]) {
    return CAROUSEL_STYLE_ALIASES[v];
  }
  return "legacy";
}

export function getCarouselPlayMode(style) {
  if (isLegacyCarousel(style)) {
    return "legacy";
  }
  const ext = ensureCarouselExt(style);
  const v = ext.playMode || "legacy";
  return CAROUSEL_PLAY_MODES.indexOf(v) > -1 ? v : "legacy";
}

/** 归一化后的样式名（不含尺寸判断） */
export function getCarouselVisualStyle(style) {
  if (isLegacyCarousel(style)) {
    return "legacy";
  }
  const ext = ensureCarouselExt(style);
  return normalizeCarouselStyle(ext.carouselStyle);
}

/**
 * 实际参与渲染的样式。
 * 尺寸不再参与判断：所有几何量都按 u = min(W,H) 等比推导，小尺寸只是整体缩小，
 * 不会因为体积小而偷偷换成别的样式。见 docs §4.6。
 */
export function resolveCarouselStyle(style) {
  return getCarouselVisualStyle(style);
}

export function shouldAutoplay(style, picCount) {
  if (picCount < 2) {
    return false;
  }
  const mode = getCarouselPlayMode(style);
  if (mode === "legacy" || mode === "auto" || mode === "autoAndManual") {
    return true;
  }
  return false;
}

export function mergeTransition(style) {
  const ext = ensureCarouselExt(style);
  const t = { ...DEFAULT_TRANSITION, ...(ext.transition || {}) };
  const legacy = isLegacyCarousel(style);
  return {
    durationMs: legacy ? 450 : Number(t.durationMs) || 450,
    type: legacy ? "fade" : t.type || "fade",
  };
}

/** 编辑器：写入 carouselExt 前确保对象存在（并升级 version） */
export function initCarouselExtOnStyle(style) {
  if (!style || typeof style !== "object") {
    return;
  }
  if (!style.carouselExt || typeof style.carouselExt !== "object") {
    Vue.set(style, "carouselExt", { schemaVersion: CAROUSEL_EXT_SCHEMA_VERSION });
  }
  if (style.carouselExt.schemaVersion !== CAROUSEL_EXT_SCHEMA_VERSION) {
    Vue.set(style.carouselExt, "schemaVersion", CAROUSEL_EXT_SCHEMA_VERSION);
  }
}

export const CAROUSEL_EXT_ATTR_MAP = {
  carouselPlayMode: "playMode",
  carouselVisualStyle: "carouselStyle",
};

export function getExtFieldByAttrKey(style, attrKey) {
  const path = CAROUSEL_EXT_ATTR_MAP[attrKey];
  if (!path) {
    return "";
  }
  const ext = ensureCarouselExt(style);
  if (!ext || Object.keys(ext).length === 0) {
    return "legacy";
  }
  if (path === "playMode") {
    const v = ext.playMode || "legacy";
    return CAROUSEL_PLAY_MODES.indexOf(v) > -1 ? v : "legacy";
  }
  return normalizeCarouselStyle(ext.carouselStyle);
}

export function setExtFieldByAttrKey(style, attrKey, val) {
  initCarouselExtOnStyle(style);
  const path = CAROUSEL_EXT_ATTR_MAP[attrKey];
  if (!path) {
    return;
  }
  const next = path === "carouselStyle" ? normalizeCarouselStyle(val) : val;
  Vue.set(style.carouselExt, path, next);
}

/** 画布组件 :key，样式变更时强制重挂载以便立即切换 UI */
export function carouselEditorRenderKey(item) {
  if (!item) {
    return "cmp-unknown";
  }
  if (item.component !== "Carousel") {
    return String(item.id != null ? item.id : item.component || "cmp");
  }
  const ext = item.style && item.style.carouselExt;
  const baseId = item.id != null ? String(item.id) : "carousel";
  if (!ext) {
    return baseId;
  }
  return [
    baseId,
    resolveCarouselStyle(item.style),
    ext.playMode || "legacy",
  ].join("-");
}

export function clearCarouselExtOnStyle(style) {
  if (!style || !style.carouselExt) {
    return;
  }
  Vue.delete(style, "carouselExt");
}
